import { css } from "@linaria/core";
import cx from "classnames";
import React from "react";
import type { BlockCommentModelT } from "../../../../../schema/block/comment";
import { MessageEditorV2 } from "@ui/MessageEditorV2";
import { usePost } from "../../../../../../../../api/useCall";
import { useComments } from "../../../../../../../../api/useComments";
import type { Project } from "../../../../../../../../api/useProjects";
import t from "../../../../../../../../i18n";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { PencilLine } from "@ui/IconV2/react";
import formatDate from "@ui/i18n/formatDate";
import { useState, useRef } from "react";
import useToasts from "@ui/Toast/useToasts";
import { canUserDoOnProject } from "diagnostics-domain/canUserDo";
import { useUser } from "../../../../../../../../api/useUser";
import TooltipV2 from "@ui/TooltipV2";
import { colors, fonts, lineHeights, spacings } from "@ui/Theme";
import { Markdown } from "@ui/Markdown";
import { ToolbarSeparator } from "@ui/Markdown/Toolbar/MarkdownToolbarTable";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import TabFocusPlugin from "@ui/Markdown/plugins/Table/plugins/TableFocusPlugin";
import { TableCellResizerPlugin } from "@ui/Markdown/plugins/Table/plugins/TableCellResizerPlugin";
import { TablePasteSanitizerPlugin } from "@ui/Markdown/plugins/Table/plugins/TablePasteSanitizerPlugin";
import { DisableFormattingPlugin } from "@ui/Markdown/plugins/Table/plugins/DisableFormattingPlugin";
import { TableToolbarButton } from "@ui/Markdown/plugins/Table/components/TableToolbarButton";
import { DEFAULT_CONFIG_TABLE } from "@ui/Markdown/plugins/Table/utils/config";
import { DEFAULT_CLASSNAME_THEME_TABLE } from "@ui/Markdown/plugins/Table/utils/style";

export const BlockComment = React.memo(
  ({
    block,
    project,
    isDashboardEditing,
  }: {
    block: BlockCommentModelT;
    project: Project;
    isDashboardEditing: boolean;
  }) => {
    const createComment = usePost("/comments");
    const comments = useComments(project.id);
    const { addToast } = useToasts();
    const user = useUser();

    const canEdit = canUserDoOnProject(user, "PROJECT_WRITE", project.id);
    const [isEditing, setIsEditing] = useState(false);
    const [showToolbar, setShowToolbar] = useState(false);
    const editorRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLDivElement>(null);

    const lastComment = comments
      .filter(c => c.dashboardId === block.dashboardId && c.blockCommentId === block.id)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    const handlePublish = async (comment: string) => {
      if (isDashboardEditing) return;

      await createComment({
        body: {
          type: "RUN",
          projectId: project.id,
          analysisId: null,
          parentId: null,
          content: comment,
          dashboardId: block.dashboardId,
          faultEventId: null,
          manualFaultEventId: null,
          blockCommentId: block.id,
        },
      });

      setIsEditing(false);
      setShowToolbar(false);

      addToast("success", t("project.dashboard.block.comment_rich_text.editable_text.updated"));
    };

    return (
      <div
        title={
          isDashboardEditing ? t("project.dashboard.block.comment_rich_text.title") : undefined
        }
        ref={editorRef}
        className={cx(content, editorWrapper, overflowXAuto, {
          [disabledContainer]: isDashboardEditing,
        })}
      >
        <div className={editorContainer}>
          <div className={markdownEditorContainer}>
            {isEditing || !lastComment ? (
              <MessageEditorV2
                placeholder={t("project.dashboard.block.comment_rich_text.placeholder")}
                onPublish={handlePublish}
                maxLength={4000}
                onCancel={() => {
                  setIsEditing(false);
                  setShowToolbar(false);
                }}
                defaultValue={lastComment?.content}
                key={lastComment?.createdAt}
                autoFocus={isEditing}
                onFocus={() => setShowToolbar(true)}
                onBlur={e => {
                  const nextTarget = e.relatedTarget as HTMLElement | null;
                  setShowToolbar(Boolean(nextTarget?.closest("#modal-id")));
                }}
              >
                {(MarkdownToolbarTable, richText) => (
                  <>
                    {showToolbar && (
                      <>
                        <MarkdownToolbarTable>
                          <ToolbarSeparator />
                          <TableToolbarButton />
                        </MarkdownToolbarTable>
                        <TablePlugin hasHorizontalScroll />
                        <TabFocusPlugin />
                        <TableCellResizerPlugin />
                        <TablePasteSanitizerPlugin />
                        <DisableFormattingPlugin formats={["bold", "italic"]} />
                      </>
                    )}
                    <div className={textAndIconContainer} ref={textRef}>
                      <div
                        className={cx(textContainer, { [disabledTextField]: isDashboardEditing })}
                        onKeyDown={isDashboardEditing ? e => e.preventDefault() : undefined}
                      >
                        {richText}
                      </div>
                    </div>
                  </>
                )}
              </MessageEditorV2>
            ) : (
              <div className={textAndIconContainer}>
                <Markdown
                  key={lastComment?.createdAt}
                  defaultValue={lastComment?.content ?? ""}
                  editable={false}
                  config={{
                    nodes: [...DEFAULT_CONFIG_TABLE.nodes],
                    theme: DEFAULT_CONFIG_TABLE.theme,
                  }}
                  theme={DEFAULT_CLASSNAME_THEME_TABLE}
                />
                {!isDashboardEditing && canEdit && (
                  <div className={iconWrapper}>
                    <div className={iconContainer}>
                      <TooltipV2
                        overlay={t("project.dashboard.block.comment_rich_text.text.tooltip")}
                      >
                        <ButtonIconV2 onClick={() => setIsEditing(true)}>
                          <PencilLine className={hideOnPrint} />
                        </ButtonIconV2>
                      </TooltipV2>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {lastComment && !isEditing && (
            <div className={footerText}>
              {t("project.dashboard.block.comment_rich_text.modified_by", {
                author: lastComment.author.name ?? lastComment.author.id,
                date: formatDate(lastComment.createdAt, "slash", project.timezone),
              })}
            </div>
          )}
        </div>
      </div>
    );
  },
);

//#region CSS
const content = css`
  font: inherit;
  max-width: 100%;
  line-break: strict;
  white-space: pre-wrap;
  position: relative;
`;

const editorWrapper = css`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: ${spacings.xs};
`;

const editorContainer = css`
  width: 100%;
  height: 100%;
  padding-bottom: ${spacings.xs};
  border-radius: ${spacings.xxs};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${spacings.xs};
`;

const markdownEditorContainer = css`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const textAndIconContainer = css`
  align-self: stretch;
  padding-top: ${spacings.xs};
  padding-left: ${spacings.m};
  padding-right: ${spacings.s};
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 10px;
  width: 100%;
`;

const textContainer = css`
  flex: 1;
  color: ${colors.carbon900};
  font: ${fonts.body1};
  word-wrap: break-word;
  overflow-x: auto;
`;

const iconWrapper = css`
  padding: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const iconContainer = css`
  width: ${spacings.m};
  height: ${spacings.m};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const footerText = css`
  flex: 1;
  height: 18px;
  padding-left: ${spacings.m};
  padding-right: ${spacings.s};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${spacings.xxs};
  color: ${colors.carbon800};
  font-size: ${spacings.s};
  line-height: ${lineHeights[1]};
  word-wrap: break-word;
`;

const disabledContainer = css`
  opacity: 0.5;
  cursor: not-allowed;
`;

const disabledTextField = css`
  user-select: none;
  cursor: default;
  pointer-events: none;
`;

const hideOnPrint = css`
  @media print {
    display: none;
  }
`;

const overflowXAuto = css`
  overflow-x: auto;
`;

//#endregion CSS
